import React from "react";
import Container from "./container";
import ReactSlider from "react-slider";
import { remCalc } from "../../theme/style-utils-coras-nua";
import defaultTheme from "../../theme/theme-test-override";
import { ThemeProps, ThemeTemplate } from "../../theme/theme-template";
import styled from "styled-components";
import { iControlProps } from "../types";

type Props = iControlProps & {
    type: string;
    value?: number;
    boi_slider_increment_value?: number;
    boi_slider_range_label?: string;
    boi_slider_range_min?: number;
    boi_slider_range_max?: number;
};

const StyledSlider = styled(ReactSlider)`
    width: 100%;
    height: ${remCalc(16)};
    margin-top: ${remCalc(16)};
    margin-bottom: ${remCalc(20.8)};
    & div {
        outline: none;
    }
`;

const StyledThumbContainer = styled.div`
    transition: left 0.3s ease-out;
    & .react-slider-label-container { 
        top: -${remCalc(24)};
    }
    & > .react-slider-label-container > .react-slider-label {
        left: -50%;
    }
`;

const StyledThumb = styled.div<ThemeProps>`
    position: absolute;
    top: 50%;
    height: ${remCalc(16)};
    width: ${remCalc(16)};
    margin-left: -${remCalc(8)};
    background: ${props => props.theme.colors.primary};
    border: ${remCalc(1)} solid ${props => props.theme.colors.primary};
    border-radius: 100%;
    cursor: pointer;
    transition: transform 0.3s ease-out, box-shadow 0.3s ease-out;
    &:active {
        transform: scale(1.3);
    }
`;
StyledThumb.defaultProps = {
    theme: {
        ...defaultTheme
    } as ThemeTemplate
};

const StyledSliderLabelContainer = styled.span`
    position: absolute;
`;
StyledSliderLabelContainer.defaultProps = {
    className: "react-slider-label-container"
};

const StyledSliderLabel = styled.span<ThemeProps>`
    position: relative;
    color: ${props => props.theme.colors.monotoneMidDark};
    font-size: ${remCalc(12.8)};
    white-space: nowrap;
`;
StyledSliderLabel.defaultProps = {
    className: "react-slider-label",
    theme: {
        ...defaultTheme
    } as ThemeTemplate
};

const StyledTrack = styled.div<ThemeProps>`
    top: 50%;
    bottom: 0;
    height: ${remCalc(4.8)};
    margin-top: -${remCalc(2.4)};
    border-radius: ${remCalc(4.8)};
    cursor: pointer;
    &.track-0 {
        background: ${props => props.theme.colors.primary};
        transition: right 0.3s ease-out, width 0.3s ease-out;
        & > .react-slider-label-container {
            left: 0;
        }
        & > .react-slider-label-container > .react-slider-label {
            left: -50%;
        }
    }
    &.track-1 {
        background: ${props => props.theme.colors.monotoneMidLight};
        transition: left 0.3s ease-out, width 0.3s ease-out;
        & > .react-slider-label-container {
            right: 0;
        }
        & > .react-slider-label-container > .react-slider-label {
            left: 50%;
        }
    }
    & > .react-slider-label-container {
        top: ${remCalc(12.8)};
    }
`;
StyledTrack.defaultProps = {
    theme: {
        ...defaultTheme
    } as ThemeTemplate
};

const Thumb = (
    props: any,
    formattedValue: string
) => (
    <StyledThumbContainer {...props}>
        <StyledSliderLabelContainer>
            <StyledSliderLabel>{formattedValue}</StyledSliderLabel>
        </StyledSliderLabelContainer>
        <StyledThumb />
    </StyledThumbContainer>
);

const Track = (
    props: any,
    state: { index: number; value: number | readonly number[] },
    formattedValue: string
) => (
    <StyledTrack {...props} index={state.index}>
        <StyledSliderLabelContainer>
            <StyledSliderLabel>{formattedValue}</StyledSliderLabel>
        </StyledSliderLabelContainer>
    </StyledTrack>
);

const Slider = ({
    value,
    boi_slider_increment_value,
    boi_slider_range_label,
    boi_slider_range_min,
    boi_slider_range_max,
    onChange,
    ...props
}: Props) => {
    const rangeMin = Number(boi_slider_range_min) || 0;
    const rangeMax = Number(boi_slider_range_max) || 100;

    return (
        <Container {...props}>
            <StyledSlider
                className="react-slider"
                step={Number(boi_slider_increment_value) || 1}
                min={rangeMin}
                max={rangeMax}
                value={Number(value)}
                onChange={value => {
                    onChange && onChange(value);
                }}
                renderTrack={(props, state) => {
                    state.value = !state.index ? rangeMin : rangeMax;
                    const formattedValue = boi_slider_range_label
                        ? state.value + " " + boi_slider_range_label
                        : String(state.value);
                    return Track(props, state, formattedValue);
                }}
                renderThumb={(props, state) => {
                    const formattedValue = boi_slider_range_label
                        ? state.valueNow + " " + boi_slider_range_label
                        : String(state.valueNow);
                    return Thumb(props, formattedValue);
                }}
            />
        </Container>
    );
};

export default Slider;
